// RelatorioProducaoCapsulas.js - Relatório de Produção de Cápsulas por Funcionário
import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, Grid, Chip, Alert, CircularProgress,
  TablePagination, Tooltip
} from '@mui/material';
import {
  FilterList, Print, Refresh
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

const RelatorioProducaoCapsulas = () => {
  const { api } = useAuth();

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dados, setDados] = useState(null);

  // Filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: ''
  });

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Métrica: capsulas ou requisicoes
  const [metrica, setMetrica] = useState('capsulas');

  // Definir período padrão: mês atual
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: hoje.toISOString().split('T')[0]
    }));
  }, []);

  // Carregar dados
  const carregarDados = useCallback(async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.relatorioProducaoCapsulas({ ...filtros, setor: 'Encapsulação' });
      const d = res?.dados || res?.data || res;
      setDados(d);
      setPage(0);
    } catch (e) {
      console.error('Erro ao carregar dados:', e);
      setError('Erro ao carregar dados: ' + (e.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  }, [api, filtros]);

  // Dados memoizados
  const funcionarios = useMemo(() => dados?.funcionarios || [], [dados]);
  const dias = useMemo(() => dados?.dias || [], [dados]);
  const totaisDiarios = useMemo(() => dados?.totaisDiarios || {}, [dados]);
  const resumo = useMemo(() => dados?.resumo || {}, [dados]);

  const funcPaginados = useMemo(() =>
    funcionarios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [funcionarios, page, rowsPerPage]
  );

  // Weekend set memoizado
  const weekendSet = useMemo(() => {
    const set = new Set();
    dias.forEach(dia => {
      const d = new Date(dia.data + 'T12:00:00');
      if (d.getDay() === 0 || d.getDay() === 6) set.add(dia.data);
    });
    return set;
  }, [dias]);

  // Cores por faixa
  const obterCorCelula = useCallback((valor) => {
    if (valor === 0) return '#ffebee';
    if (valor < 30) return '#fff3e0';
    if (valor < 60) return '#f3e5f5';
    if (valor < 120) return '#e8f5e9';
    if (valor < 300) return '#e3f2fd';
    return '#c8e6c9';
  }, []);

  const obterCorTexto = useCallback((valor) => {
    if (valor === 0) return '#c62828';
    if (valor < 30) return '#ef6c00';
    if (valor < 60) return '#7b1fa2';
    if (valor < 120) return '#2e7d32';
    if (valor < 300) return '#1565c0';
    return '#1b5e20';
  }, []);

  // Impressão
  const handlePrint = () => {
    if (!dados || funcionarios.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Permita pop-ups para imprimir.');
      return;
    }

    const metricaLabel = metrica === 'capsulas' ? 'Cápsulas' : 'Requisições';

    let html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Produção de Cápsulas - ${filtros.dataInicio} a ${filtros.dataFim}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 10px; margin: 10px; }
        h2 { text-align: center; margin-bottom: 5px; }
        .info { text-align: center; color: #666; margin-bottom: 10px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ccc; padding: 3px 5px; text-align: center; }
        th { background: #f5f5f5; font-weight: bold; }
        .nome { text-align: left; min-width: 150px; }
        .setor { text-align: left; min-width: 80px; }
        .total { background: #e3f2fd; font-weight: bold; }
        .zero { color: #ccc; }
        .rodape { background: #e8f5e9; font-weight: bold; }
        @media print { body { margin: 5mm; } }
      </style></head><body>
      <h2>Produção de Cápsulas</h2>
      <div class="info">${filtros.dataInicio} a ${filtros.dataFim} | ${metricaLabel} | ${funcionarios.length} funcionários</div>
      <table><thead><tr><th class="setor">Setor</th><th class="nome">Funcionário</th>`;

    dias.forEach(d => { html += `<th>${d.dia}<br/>${d.diaSemana}</th>`; });
    html += `<th class="total">Total</th></tr></thead><tbody>`;

    funcionarios.forEach(func => {
      html += `<tr><td class="setor">${escapeHtml(func.setor)}</td><td class="nome">${escapeHtml(func.nome)}</td>`;
      dias.forEach(d => {
        const prod = func.producaoDiaria[d.data];
        const val = prod ? (metrica === 'capsulas' ? prod.capsulas : prod.requisicoes) : 0;
        html += `<td class="${val === 0 ? 'zero' : ''}">${val > 0 ? val.toLocaleString('pt-BR') : '-'}</td>`;
      });
      const total = metrica === 'capsulas' ? func.totalCapsulas : func.totalRequisicoes;
      html += `<td class="total">${total.toLocaleString('pt-BR')}</td></tr>`;
    });

    // Rodapé com totais
    html += `<tr class="rodape"><td colspan="2"><strong>TOTAL</strong></td>`;
    dias.forEach(d => {
      const t = totaisDiarios[d.data];
      const val = t ? (metrica === 'capsulas' ? t.capsulas : t.requisicoes) : 0;
      html += `<td>${val > 0 ? val.toLocaleString('pt-BR') : '-'}</td>`;
    });
    const totalGeral = metrica === 'capsulas' ? resumo.totalCapsulas : resumo.totalRequisicoes;
    html += `<td class="total">${(totalGeral || 0).toLocaleString('pt-BR')}</td></tr>`;

    html += `</tbody></table></body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        💊 Produção de Cápsulas
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Início"
              type="date"
              fullWidth
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Fim"
              type="date"
              fullWidth
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Métrica</InputLabel>
              <Select
                value={metrica}
                label="Métrica"
                onChange={(e) => {
                  const val = e.target.value;
                  startTransition(() => setMetrica(val));
                }}
              >
                <MenuItem value="capsulas">Quantidade de Cápsulas</MenuItem>
                <MenuItem value="requisicoes">Requisições Únicas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={carregarDados}
                disabled={loading}
              >
                Buscar
              </Button>
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={funcionarios.length === 0}
              >
                Imprimir
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                Limpar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Cards resumo */}
      {dados && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2.4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {(resumo.totalCapsulas || 0).toLocaleString('pt-BR')}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Cápsulas</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {(resumo.totalRequisicoes || 0).toLocaleString('pt-BR')}
                </Typography>
                <Typography variant="body2" color="text.secondary">Requisições</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {resumo.totalFuncionarios || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Funcionários</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {(resumo.mediaPorFuncionario || 0).toLocaleString('pt-BR')}
                </Typography>
                <Typography variant="body2" color="text.secondary">Média/Funcionário</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {(resumo.mediaPorDia || 0).toLocaleString('pt-BR')}
                </Typography>
                <Typography variant="body2" color="text.secondary">Média/Dia</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tabela */}
      {funcionarios.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold', backgroundColor: '#f5f5f5',
                      minWidth: '100px', position: 'sticky', left: 0, zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Setor
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold', backgroundColor: '#f5f5f5',
                      minWidth: '180px', position: 'sticky', left: '100px', zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Funcionário
                  </TableCell>

                  {dias.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold', backgroundColor: weekendSet.has(dia.data) ? '#eeeeee' : '#f5f5f5',
                        minWidth: '55px', fontSize: '0.72rem', padding: '6px 3px'
                      }}
                    >
                      <Box>
                        <Typography variant="caption" display="block">{dia.dia}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {dia.diaSemana}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}

                  <TableCell
                    align="center"
                    sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd', minWidth: '80px' }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {funcPaginados.map((func, index) => (
                  <TableRow
                    key={func.codigo}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 'bold', border: '1px solid #e0e0e0',
                        position: 'sticky', left: 0, zIndex: 2,
                        backgroundColor: 'background.paper',
                        borderRight: '2px solid #e0e0e0', minWidth: '100px'
                      }}
                    >
                      <Chip label={func.setor} size="small" color="primary" variant="outlined" />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold', border: '1px solid #e0e0e0',
                        position: 'sticky', left: '100px', zIndex: 2,
                        backgroundColor: 'background.paper',
                        borderRight: '2px solid #e0e0e0', minWidth: '180px'
                      }}
                    >
                      {func.nome}
                    </TableCell>

                    {dias.map(dia => {
                      const prod = func.producaoDiaria[dia.data];
                      const valor = prod ? (metrica === 'capsulas' ? prod.capsulas : prod.requisicoes) : 0;
                      const isWeekend = weekendSet.has(dia.data);

                      return (
                        <Tooltip
                          key={dia.data}
                          title={prod ? `${prod.capsulas.toLocaleString('pt-BR')} cáps. | ${prod.requisicoes} req.` : 'Sem produção'}
                          arrow
                          placement="top"
                        >
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor: obterCorCelula(valor),
                              color: obterCorTexto(valor),
                              fontWeight: valor > 0 ? 'bold' : 'normal',
                              border: '1px solid #e0e0e0',
                              minWidth: '55px', fontSize: '0.8rem',
                              opacity: isWeekend ? 0.7 : 1,
                              cursor: 'default'
                            }}
                          >
                            {valor > 0 ? valor.toLocaleString('pt-BR') : '-'}
                          </TableCell>
                        </Tooltip>
                      );
                    })}

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold', backgroundColor: '#e3f2fd',
                        color: '#1565c0', border: '1px solid #e0e0e0', fontSize: '0.9rem'
                      }}
                    >
                      {metrica === 'capsulas'
                        ? func.totalCapsulas.toLocaleString('pt-BR')
                        : func.totalRequisicoes.toLocaleString('pt-BR')}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linha de totais */}
                <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                  <TableCell
                    sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#e8f5e9' }}
                    colSpan={2}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">TOTAL GERAL</Typography>
                  </TableCell>

                  {dias.map(dia => {
                    const t = totaisDiarios[dia.data];
                    const val = t ? (metrica === 'capsulas' ? t.capsulas : t.requisicoes) : 0;
                    return (
                      <TableCell
                        key={dia.data}
                        align="center"
                        sx={{
                          fontWeight: 'bold', backgroundColor: '#c8e6c9',
                          color: '#1b5e20', border: '1px solid #e0e0e0', fontSize: '0.8rem'
                        }}
                      >
                        {val > 0 ? val.toLocaleString('pt-BR') : '-'}
                      </TableCell>
                    );
                  })}

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold', backgroundColor: '#bbdefb',
                      color: '#0d47a1', border: '1px solid #e0e0e0', fontSize: '1rem'
                    }}
                  >
                    {metrica === 'capsulas'
                      ? (resumo.totalCapsulas || 0).toLocaleString('pt-BR')
                      : (resumo.totalRequisicoes || 0).toLocaleString('pt-BR')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={funcionarios.length}
            page={page}
            onPageChange={(e, p) => startTransition(() => setPage(p))}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const v = parseInt(e.target.value, 10);
              startTransition(() => { setRowsPerPage(v); setPage(0); });
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      )}

      {/* Legenda */}
      {funcionarios.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Legenda de Cores (Cápsulas)</Typography>
          <Grid container spacing={1}>
            {[
              { cor: '#ffebee', label: '0', desc: 'Sem produção' },
              { cor: '#fff3e0', label: '1-29', desc: 'Baixa' },
              { cor: '#f3e5f5', label: '30-59', desc: 'Média' },
              { cor: '#e8f5e9', label: '60-119', desc: 'Boa' },
              { cor: '#e3f2fd', label: '120-299', desc: 'Alta' },
              { cor: '#c8e6c9', label: '300+', desc: 'Excelente' }
            ].map(item => (
              <Grid item key={item.label}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: item.cor, border: '1px solid #ccc', borderRadius: 1 }} />
                  <Typography variant="caption">{item.label} ({item.desc})</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioProducaoCapsulas;
